import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Truck, Edit, Eye, Trash2, ShieldCheck } from 'lucide-react';
import { StatusBadge } from './StatusBadge';

// Compact date formatter - parses YYYY-MM-DD as local date to avoid timezone shift
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length >= 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      const dayNum = date.getDate();
      const monthStr = date.toLocaleDateString('en-IN', { month: 'short' });
      return `${dayNum} ${monthStr}`;
    }
    return '-';
  } catch {
    return '-';
  }
};

// Get all invoice numbers for display
const getInvoiceNumbers = (load) => {
  const nums = [];
  if (load.sve_load_type === 'Addition/Deduction') {
    if (load.summary_generated_invoice_number) nums.push(load.summary_generated_invoice_number);
    return nums.length ? nums : ['No Invoice'];
  }
  if (load.generated_invoice_number) nums.push(load.generated_invoice_number);
  if (load.generated_invoice_number_2) nums.push(load.generated_invoice_number_2);
  if (!nums.length && load.manual_invoice_number) nums.push(load.manual_invoice_number);
  if (!nums.length && load.invoice_number) nums.push(load.invoice_number);
  return nums.length ? nums : ['No Invoice'];
};

export function LoadCard({ 
  load, 
  isApprover, 
  isSuperUser,
  canDelete, 
  onView, 
  onEdit, 
  onAdminEdit,
  onDelete, 
  onDownloadFile 
}) {
  const isSVEPaid = load.client_name === 'SVE CASTINGS PVT LTD' && load.bill_status === 'paid';
  const isRejected = load.acceptance_status === 'rejected';
  const canEdit = isApprover && !isSVEPaid && !isRejected && (load.bill_status !== 'paid' || isSuperUser);
  const isSveAdditionDeduction = load.client_name === 'SVE CASTINGS PVT LTD' && load.sve_load_type === 'Addition/Deduction';
  
  const invoiceNums = getInvoiceNumbers(load);
  const dateSent = isSveAdditionDeduction 
    ? (load.summary_from_date || (load.created_at ? load.created_at.split('T')[0] : null))
    : load.date_sent;
  const netTons = isSveAdditionDeduction 
    ? Math.abs(Number(load.summary_net_tons || 0)).toFixed(3)
    : (load.tons || load.total_tons || '-');
  const finalAmount = isSveAdditionDeduction
    ? Math.abs(Number(load.summary_net_amount || 0))
    : (load.final_invoice_amount || load.invoice_amount || 0);

  const isPaid = load.bill_status === 'paid';
  const isPartiallyPaid = load.bill_status === 'partially_paid';
  const isPending = load.bill_status !== 'paid' && load.bill_status !== 'partially_paid';
  const hasDaysRemaining = isPending && load.payment_due_days_remaining !== undefined;
  const daysRemaining = load.payment_due_days_remaining;

  // Transport + Profit computation — shown on every card. Rate is understood
  // to INCLUDE transport, so:
  //   Transport total = transport_per_kg × kgs
  //   Profit total    = (rate − transport_per_kg) × kgs   (clamped at 0)
  // Source of truth for transport_per_kg:
  //   1. Explicit `load.transport_rate` on the load (set via the Adhoc dialog).
  //   2. Falls back to the hardcoded per-client values in silica_loads.py
  //      aggregation (lines 1545-1553): Balaji ₹0.75/kg, SVE max(rate−2.95, 0),
  //      everything else 0. This keeps the card in sync with the Profit Report.
  const explicitTransport = Number(load.transport_rate || 0);
  const kgsForProfit = Number(load.kgs || 0);
  const rateForProfit = Number(load.rate || 0);
  let transportRate = explicitTransport;
  if (!transportRate) {
    if (load.client_name === 'BALAJI CASTINGS PVT LTD') {
      transportRate = 0.75;
    } else if (load.client_name === 'SVE CASTINGS PVT LTD') {
      transportRate = Math.max(rateForProfit - 2.95, 0);
    }
  }
  const showTransport = transportRate > 0 && kgsForProfit > 0 && rateForProfit > 0;
  const transportTotal = showTransport ? transportRate * kgsForProfit : 0;
  const profitTotal = showTransport ? Math.max(rateForProfit - transportRate, 0) * kgsForProfit : 0;
  const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

  const isOverdue = isPending && hasDaysRemaining && daysRemaining < 0;
  const isDeliveryPending = isPending && load.delivery_status !== 'delivered' && load.sve_load_type !== 'Addition/Deduction';
  const isBillPending = isPending && !isPartiallyPaid && (load.delivery_status === 'delivered' || load.sve_load_type === 'Addition/Deduction');

  let borderColor = 'bg-slate-300';
  let categoryLabel = null;
  let categoryColors = '';
  if (isPaid) {
    borderColor = 'bg-emerald-500';
    categoryLabel = 'PAID';
    categoryColors = 'bg-emerald-50 text-emerald-700';
  } else if (isPartiallyPaid) {
    borderColor = 'bg-teal-400';
    categoryLabel = 'PARTIALLY PAID';
    categoryColors = 'bg-teal-50 text-teal-700';
  } else if (isOverdue) {
    borderColor = 'bg-red-500';
    categoryLabel = 'PAST DUE';
    categoryColors = 'bg-red-50 text-red-600';
  } else if (isDeliveryPending) {
    borderColor = 'bg-orange-400';
    categoryLabel = 'DELIVERY';
    categoryColors = 'bg-orange-50 text-orange-600';
  } else if (isBillPending) {
    borderColor = 'bg-amber-400';
    categoryLabel = 'BILL PENDING';
    categoryColors = 'bg-amber-50 text-amber-600';
  }

  // Days display helper
  const daysDisplay = hasDaysRemaining
    ? { text: daysRemaining < 0 ? `${Math.abs(daysRemaining)}d late` : `${daysRemaining}d left`, color: daysRemaining < 0 ? 'text-red-600' : daysRemaining <= 7 ? 'text-amber-600' : 'text-purple-600' }
    : isPaid && load.days_to_payment !== undefined
      ? { text: `${load.days_to_payment}d`, color: 'text-emerald-600' }
      : null;

  const formattedAmount = Number(finalAmount) ? `₹${Number(finalAmount).toLocaleString('en-IN')}` : '-';

  // Amount color based on status
  const amountColor = isPaid ? 'text-emerald-600' : isPartiallyPaid ? 'text-teal-600' : isOverdue ? 'text-red-600' : isDeliveryPending ? 'text-orange-500' : isBillPending ? 'text-amber-600' : 'text-slate-700';

  return (
    <>
      {/* ===== MOBILE LAYOUT (unchanged) ===== */}
      <Card 
        data-testid="load-card"
        className={`sm:hidden px-4 py-3 rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden bg-white border-t border-x border-slate-200 border-b-[3px] ${isPaid ? 'border-b-emerald-500' : isOverdue ? 'border-b-red-500' : isDeliveryPending ? 'border-b-orange-400' : isBillPending ? 'border-b-amber-400' : 'border-b-slate-200'}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm bg-gradient-to-br from-slate-400 to-slate-500">
              <Truck className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 data-testid="load-card-heading" className="text-sm font-bold text-slate-900 truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {invoiceNums.join(' | ')}
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button data-testid="view-load-btn" variant="outline" size="sm" className="rounded-xl h-7 w-7 p-0" onClick={() => onView(load)}>
              <Eye className="w-3.5 h-3.5" />
            </Button>
            {canEdit && load.bill_status !== 'paid' && (
              <Button variant="outline" size="sm" className="rounded-xl h-7 w-7 p-0" onClick={() => onEdit(load)} data-testid={`edit-load-${load.load_id}`}>
                <Edit className="w-3.5 h-3.5" />
              </Button>
            )}
            {canDelete && (
              <Button data-testid="delete-load-btn" variant="outline" size="sm" className="rounded-xl h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => onDelete(load.load_id)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-x-3 gap-y-2 mt-2.5 text-xs">
          <div className="min-w-0 overflow-hidden">
            <span className="text-slate-400 block" style={{ fontSize: '10px' }}>Client</span>
            <p className="font-semibold text-slate-700 truncate">{load.client_name}</p>
          </div>
          <div className="min-w-0 overflow-hidden">
            <span className="text-slate-400 block" style={{ fontSize: '10px' }}>Sent</span>
            <p className="font-medium text-slate-700">{formatDate(dateSent)}</p>
          </div>
          <div className="min-w-0 overflow-hidden">
            <span className="text-slate-400 block" style={{ fontSize: '10px' }}>Net Tons</span>
            <p className="font-bold text-slate-900">{netTons} T</p>
          </div>
          <div className="min-w-0 overflow-hidden">
            <span className="text-slate-400 block" style={{ fontSize: '10px' }}>Final Amt</span>
            <p className={`font-bold truncate ${amountColor}`}>{formattedAmount}</p>
          </div>
          <div className="min-w-0 overflow-hidden">
            <span className="text-slate-400 block" style={{ fontSize: '10px' }}>{isPaid ? 'Paid' : 'Expected'}</span>
            <p className={`font-medium ${isPaid ? 'text-emerald-700' : 'text-slate-700'}`}>
              {isPaid ? formatDate(load.actual_payment_date) : formatDate(load.expected_payment_date)}
            </p>
          </div>
          <div className="min-w-0 overflow-hidden">
            <span className="text-slate-400 block" style={{ fontSize: '10px' }}>Days</span>
            {daysDisplay ? (
              <p className={`font-bold ${daysDisplay.color}`}>{daysDisplay.text}</p>
            ) : (
              <p className="text-slate-400">-</p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mt-2.5">
          <div>
            <span className="text-slate-400 block text-xs" style={{ fontSize: '10px' }}>Status</span>
            <div className="mt-0.5"><StatusBadge status={load.bill_status} type="bill" /></div>
          </div>
          {categoryLabel && (
            <span data-testid="load-card-category-mobile" className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${categoryColors}`}>
              {categoryLabel}
            </span>
          )}
        </div>
        {isSuperUser && onAdminEdit && (
          <div className="flex justify-center mt-2.5 pt-2 border-t border-slate-100">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-3 rounded-full text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
              onClick={() => onAdminEdit(load)}
              data-testid={`master-edit-mobile-${load.load_id}`}
            >
              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Master Edit
            </Button>
          </div>
        )}
        {showTransport && (
          <div data-testid="load-card-transport-mobile"
               className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
            <span className="text-slate-500">
              Transport <span className="font-semibold text-slate-700">{inr(transportTotal)}</span>
              <span className="text-slate-300 mx-1">·</span>
              ₹{transportRate}/kg
            </span>
            <span className="text-emerald-700 font-semibold">
              Profit {inr(profitTotal)}
            </span>
          </div>
        )}
      </Card>

      {/* ===== DESKTOP LAYOUT (Option F - Two-Column Split) ===== */}
      <div
        data-testid="load-card-desktop"
        className="hidden sm:flex bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow"
      >
        {/* Colored left edge */}
        <div className={`w-1.5 flex-shrink-0 ${borderColor}`} />

        {/* Content column: main row + optional master-edit footer */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 p-4 flex items-center gap-5">
            {/* Left section: Invoice + Client + Dates */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5">
                <h3 data-testid="load-card-heading-desktop" className="font-extrabold text-slate-900 text-base truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {invoiceNums.join(' | ')}
                </h3>
                {categoryLabel && (
                  <span data-testid="load-card-category" className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide whitespace-nowrap ${categoryColors}`}>
                    {categoryLabel}
                  </span>
                )}
              </div>
              <p className="text-slate-400 text-xs mt-0.5 truncate">{load.client_name}</p>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="text-slate-400">Sent <span className="text-slate-600 font-medium">{formatDate(dateSent)}</span></span>
                <span className="text-slate-400">{isPaid ? 'Paid' : 'Expected'} <span className={`font-medium ${isPaid ? 'text-emerald-600' : 'text-slate-600'}`}>{isPaid ? formatDate(load.actual_payment_date) : formatDate(load.expected_payment_date)}</span></span>
                {daysDisplay && (
                  <span className={`font-bold ${daysDisplay.color}`}>{daysDisplay.text}</span>
                )}
              </div>
            </div>

            {/* Right section: Amount + Tons */}
            <div className="text-right flex-shrink-0 pl-5 border-l border-slate-100">
              <p className={`text-sm font-extrabold ${amountColor}`}>{formattedAmount}</p>
              <p className="text-slate-400 text-xs mt-0.5">{netTons} T</p>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-1 flex-shrink-0 ml-2">
              <Button data-testid="view-load-btn" variant="outline" size="sm" className="rounded-lg h-7 w-7 p-0" onClick={() => onView(load)}>
                <Eye className="w-3.5 h-3.5" />
              </Button>
              {canEdit && load.bill_status !== 'paid' && (
                <Button variant="outline" size="sm" className="rounded-lg h-7 w-7 p-0" onClick={() => onEdit(load)} data-testid={`edit-load-${load.load_id}`}>
                  <Edit className="w-3.5 h-3.5" />
                </Button>
              )}
              {canDelete && (
                <Button data-testid="delete-load-btn" variant="outline" size="sm" className="rounded-lg h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => onDelete(load.load_id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>

          {/* Master Edit — super user only, opens the load with ALL locks bypassed */}
          {isSuperUser && onAdminEdit && (
            <div className="px-4 pb-3 -mt-1 flex justify-center border-t border-slate-100 pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-3 rounded-full text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                onClick={() => onAdminEdit(load)}
                data-testid={`master-edit-desktop-${load.load_id}`}
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Master Edit
              </Button>
            </div>
          )}
          {showTransport && (
            <div data-testid="load-card-transport-desktop"
                 className="px-4 pb-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
              <span className="text-slate-500">
                Transport <span className="font-semibold text-slate-700">{inr(transportTotal)}</span>
                <span className="text-slate-300 mx-1.5">·</span>
                <span className="text-slate-400">₹{transportRate}/kg</span>
              </span>
              <span className="text-emerald-700 font-semibold">
                Profit {inr(profitTotal)}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Empty state component
export function EmptyLoadsState() {
  return (
    <div className="text-center py-12">
      <Truck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
      <h3 className="text-lg font-semibold text-slate-700 mb-2">No Silica Loads</h3>
      <p className="text-slate-500 mb-4">Start by creating your first silica load record</p>
    </div>
  );
}
